import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchAssessmentOutcomesData, fetchAssessmentDyslexiaRiskData } from '../../../../services/query/assessmentOutcomesAPI';
import { AssessmentOutcomesService } from '../../../../services/assessmentOutcomesService';
import { AsyncLoadingState } from '../../enums';
import { AssessmentOutcomesAPIResponseInterface, AssessmentOutcomesWidgetModel } from '../../models';
import { AssessmentOutcomesView } from '@/enums';
import { RootState } from '@/models';

export interface AssessmentDyslexiaRiskWidgetResponse {
  classroomDyslexiaRiskData: {
    data: {
      aggregateData: {
        highCount: number;
        lowCount: number;
        moderateCount: number;
      };
      students: {
        id: string;
        name: string;
        riskLevel: string;
      }[];
    };
  };
}

interface AssessmentOutcomesWidgetState {
  loading: AsyncLoadingState;
  data: AssessmentOutcomesAPIResponseInterface | null;
  classificationData: AssessmentOutcomesAPIResponseInterface | null; // Store raw data instead of model instance
  dyslexiaRiskData: AssessmentDyslexiaRiskWidgetResponse | null;
  view: AssessmentOutcomesView;
  error: boolean;
}

const initialState: AssessmentOutcomesWidgetState = {
  data: null,
  classificationData: null, // Store raw data
  dyslexiaRiskData: null,
  loading: AsyncLoadingState.LOADING,
  view: AssessmentOutcomesView.ASSESSMENT_CLASSIFICATION,
  error: false,
};

export const fetchAssessmentOutcomesWidgetData = createAsyncThunk<AssessmentOutcomesAPIResponseInterface, number>(
  'assessmentOutcomesWidget/fetchAssessmentOutcomesWidgetData',
  async (classroomId, { rejectWithValue }) => {
    if (!classroomId) {
      return rejectWithValue('classroomId is required');
    }
    try {
      // Use the new service that can switch between old and new implementations
      const response = await AssessmentOutcomesService.fetchAssessmentOutcomesData({ classroomId });
      return response as AssessmentOutcomesAPIResponseInterface;
    } catch (error: any) {
      console.error('Error fetching assessment outcomes data', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAssessmentDyslexiaRiskWidgetData = createAsyncThunk<AssessmentDyslexiaRiskWidgetResponse, number>(
  'assessmentOutcomesWidget/fetchAssessmentDyslexiaRiskWidgetData',
  async (classroomId, { rejectWithValue }) => {
    try {
      const response = await fetchAssessmentDyslexiaRiskData(classroomId);
      return response as AssessmentDyslexiaRiskWidgetResponse;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const assessmentOutcomesWidgetSlice = createSlice({
  name: 'assessmentOutcomesWidget',
  initialState,
  reducers: {
    updateAssessmentOutcomesWidgetData(state, action: PayloadAction<AssessmentOutcomesWidgetState>) {
      return action.payload;
    },
    updateAssessmentOutcomesView(state, action: PayloadAction<AssessmentOutcomesView>) {
      state.view = action.payload;
      state.error = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssessmentOutcomesWidgetData.fulfilled, (state, action: PayloadAction<AssessmentOutcomesAPIResponseInterface>) => {
        state.loading = AsyncLoadingState.SUCCESS;
        state.data = action.payload;
        state.classificationData = action.payload;
        state.error = false;
      })
      .addCase(fetchAssessmentOutcomesWidgetData.pending, (state) => {
        state.loading = AsyncLoadingState.LOADING;
        state.error = false;
      })
      .addCase(fetchAssessmentOutcomesWidgetData.rejected, (state, action) => {
        state.loading = AsyncLoadingState.ERROR;
        state.error = true;
      })
      .addCase(fetchAssessmentDyslexiaRiskWidgetData.fulfilled, (state, action: PayloadAction<AssessmentDyslexiaRiskWidgetResponse>) => {
        state.loading = AsyncLoadingState.SUCCESS;
        state.dyslexiaRiskData = action.payload;
        state.error = false;
      })
      .addCase(fetchAssessmentDyslexiaRiskWidgetData.pending, (state) => {
        state.loading = AsyncLoadingState.LOADING;
        state.error = false;
      })
      .addCase(fetchAssessmentDyslexiaRiskWidgetData.rejected, (state, action) => {
        state.loading = AsyncLoadingState.ERROR;
        state.error = true;
      });
  },
});

// Export the actions
export const { updateAssessmentOutcomesWidgetData, updateAssessmentOutcomesView } = assessmentOutcomesWidgetSlice.actions;
export const selectAssessmentOutcomesWidget = (state: RootState) => state.assessmentOutcomesWidget as AssessmentOutcomesWidgetState;
export const selectAssessmentOutcomesClassificationData = (state: RootState) => state.assessmentOutcomesWidget.classificationData;
export const selectAssessmentOutcomesDyslexiaData = (state: RootState) => state.assessmentOutcomesWidget.dyslexiaRiskData;
export default assessmentOutcomesWidgetSlice.reducer;